import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const iltmCannes: ChauffeurPage = {
  slug: "iltm-cannes",
  title: "Chauffeur privé ILTM Cannes – VTC luxe B2B",
  subtitle:
    "Chauffeur privé pendant l'ILTM à Cannes : transferts exécutifs, agendas de rendez-vous denses au Palais et runs aéroport de Nice en basse saison.",
  keywords: [
    "chauffeur iltm cannes",
    "vtc iltm cannes",
    "chauffeur privé iltm",
  ],
  hero: {
    title: "Chauffeur privé pendant l'ILTM Cannes",
    subtitle:
      "Début décembre, le rendez-vous mondial du voyage de luxe réunit au Palais acheteurs et vendeurs sur invitation : un chauffeur pour des journées de rendez-vous minutées.",
    keywords: [
      "chauffeur ILTM Cannes",
      "VTC voyage de luxe Cannes",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant l'ILTM à Cannes",
    badges: [
      "Transferts exécutifs",
      "Agenda de rendez-vous",
      "Aéroport basse saison",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant l'ILTM",
    items: [
      {
        title: "Rendez-vous minutés",
        description:
          "L'ILTM fonctionne par rendez-vous pré-planifiés de quelques minutes : ponctualité absolue exigée, un chauffeur en attente fluidifie chaque transition.",
      },
      {
        title: "Salon sur invitation",
        description:
          "Acheteurs UHNW et conseillers voyage haut de gamme : service en tenue, discrétion et standing à la hauteur de la clientèle.",
      },
      {
        title: "Aéroport en basse saison",
        description:
          "Début décembre, hors pic touristique mais météo variable : suivi de vol et accueil pour des arrivées sereines à toute heure.",
      },
      {
        title: "Hospitalité premium",
        description:
          "Dîners de réseautage, suites de marques et soirées partenaires : liaisons et retours de nuit assurés sur toute la durée.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant l'ILTM Cannes",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol pour acheteurs, exposants et conseillers voyage, même en horaires décalés de décembre.",
      },
      {
        title: "Mise à disposition exécutive",
        text: "Chauffeur dédié à la demi-journée ou à la journée pour respecter un agenda de rendez-vous serré au Palais.",
      },
      {
        title: "Tournées multi-stops",
        text: "Itinéraires optimisés entre le Palais, les hôtels et les suites de réception des partenaires.",
      },
      {
        title: "Navettes délégations",
        text: "Coordination multi-véhicules pour groupes de buyers, marques hôtelières et offices de tourisme.",
      },
      {
        title: "Dîners & soirées de réseautage",
        text: "Réceptions, dîners partenaires et soirées professionnelles, avec retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant l'ILTM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Espaces de rendez-vous, stands et suites des marques de luxe du salon.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic : hébergement des délégations et dîners de réseautage.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description:
          "Terminaux 1 et 2, arrivées internationales en basse saison début décembre.",
      },
      {
        name: "Mougins & arrière-pays",
        description:
          "Tables gastronomiques et dîners partenaires hors du centre.",
      },
      {
        name: "Antibes / Juan-les-Pins",
        description: "Hôtels complémentaires et logistique des équipes.",
      },
    ],
  },
  generic: {
    heading: "L'ILTM vu d'un chauffeur",
    subheading:
      "Début décembre, le voyage de luxe mondial se réunit au Palais sur invitation — un salon B2B où chaque rendez-vous est minuté.",
    paragraphs: [
      "Chaque début décembre, l'International Luxury Travel Market réunit à Cannes acheteurs et vendeurs du voyage haut de gamme, sur invitation. Le salon repose sur des rendez-vous pré-planifiés et minutés au Palais des Festivals : la ponctualité y est une exigence, et un chauffeur en attente entre deux blocs de meetings évite tout retard sur un agenda dense.",
      "Les délégations arrivent par l'aéroport de Nice Côte d'Azur. Nous sommes hors du pic touristique estival, mais la météo de décembre peut être variable : le suivi de vol et l'accueil garantissent des arrivées sereines, y compris en horaires décalés.",
      "L'ILTM se prolonge en hospitalité : suites de marques, dîners de réseautage et soirées partenaires rythment la Croisette et l'arrière-pays. Les tournées multi-stops et la mise à disposition gardent le même chauffeur, avec les retours de nuit après les dîners.",
    ],
    bullets: [
      "Rendez-vous minutés respectés",
      "Suivi de vol début décembre",
      "Hospitalité premium",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour l'ILTM Cannes",
    steps: [
      "Indiquez vos dates, votre hôtel et votre planning de rendez-vous au Palais.",
      "Nous construisons les tournées multi-stops et les liaisons aéroport selon votre agenda minuté.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour rendez-vous, suites de marques et dîners.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pendant l'ILTM Cannes ?",
      a: "Réservez en amont : début décembre, l'ILTM concentre la demande premium sur Cannes. Transmettez vos dates, votre hôtel et votre planning de rendez-vous pour que nous bloquions un chauffeur dédié sur toute la durée du salon.",
    },
    {
      q: "Pouvez-vous tenir un agenda de rendez-vous très serré ?",
      a: "Oui. L'ILTM fonctionne par rendez-vous pré-planifiés et minutés : nous construisons des tournées optimisées et gardons un chauffeur en attente entre vos blocs de meetings pour absorber tout décalage et préserver la ponctualité.",
    },
    {
      q: "Les transferts aéroport sont-ils assurés en basse saison ?",
      a: "Oui, avec suivi de vol et accueil. Début décembre est hors du pic estival, mais la météo peut être variable : nous adaptons l'heure de prise en charge et couvrons les arrivées internationales à toute heure.",
    },
    {
      q: "Couvrez-vous les dîners et soirées de réseautage ?",
      a: "Oui. Suites de marques, dîners partenaires et soirées de réseautage se prolongent en soirée sur la Croisette ou dans l'arrière-pays : le chauffeur patiente et garantit votre retour de nuit.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un transfert Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Réserver un transfert Cannes Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Vérifier les disponibilités",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour l'ILTM Cannes",
    text: "Transferts exécutifs, agenda de rendez-vous minuté et hospitalité premium : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "Chauffeur ILTM Cannes — VTC luxe B2B Palais des Festivals | KS Rent Services",
    description:
      "Chauffeur privé pendant l'ILTM à Cannes : transferts exécutifs, agendas denses au Palais, runs aéroport de Nice et hospitalité premium.",
    canonical: "/chauffeur/iltm-cannes",
  },
};

export default iltmCannes;
